export type FaceExpressions = {
  neutral: number;
  happy: number;
  sad: number;
  angry: number;
  fearful: number;
  disgusted: number;
  surprised: number;
};

export type StressLevelKey = 'stable' | 'comfortable' | 'slightlyTense' | 'tense';

export type StressScoreResult = {
  /** 0–100; higher = more stressed */
  score: number;
  level: StressLevelKey;
};

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Stress index from HRV + expression.
 * // ponytail: heuristic only — swap for commercial rPPG stress if needed
 */
export function computeStressScore(
  rmssdMs: number | null,
  expressions: FaceExpressions | null,
): StressScoreResult {
  const hrvPart =
    rmssdMs == null ? 50 : clamp(100 - rmssdMs * 1.1, 15, 90);

  let emotionPart = 50;
  if (expressions) {
    const tense =
      expressions.angry * 1 +
      expressions.fearful * 0.95 +
      expressions.sad * 0.75 +
      expressions.disgusted * 0.55 +
      expressions.surprised * 0.25;
    const calm =
      expressions.happy * 0.85 + expressions.neutral * 0.35;
    emotionPart = clamp((tense - calm) * 120 + 45, 10, 95);
  }

  const score = Math.round(0.55 * hrvPart + 0.45 * emotionPart);

  let level: StressLevelKey = 'comfortable';
  if (score < 35) level = 'stable';
  else if (score < 50) level = 'comfortable';
  else if (score < 65) level = 'slightlyTense';
  else level = 'tense';

  return { score, level };
}

export function topEmotionKey(
  expr: FaceExpressions,
): keyof FaceExpressions {
  const entries = Object.entries(expr) as Array<[keyof FaceExpressions, number]>;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}
